/**
 * ComparativeTable
 *
 * Mostra a comparação de fluxo de pessoas entre dois períodos.
 *
 * Layout:
 *   1. Header com os dois períodos exatos
 *   2. Cards de resumo (Atual / Variação / Anterior)
 *   3. LineChart sobreposto (tendência de ambos os períodos)
 *   4. Tabela: Data Atual | Pessoas | Data Anterior | Pessoas | Diferença | %
 */
import React, { useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import { ArrowUpRight, ArrowDownRight, Minus, TrendingUp } from 'lucide-react';
import { formatDateWithWeekday } from '../utils/computeComparativeRange';

// ─── Interfaces ────────────────────────────────────────────────────────────────

export interface DailyPoint {
  date: string;   // YYYY-MM-DD
  people: number;
}

interface ComparativeTableProps {
  current: DailyPoint[];
  previous: DailyPoint[];
  comparisonLabel: string;
  isYearOverYear?: boolean;
  className?: string;
}

interface TableRow {
  curDate: string;
  prevDate: string;
  cur: number;
  prev: number;
  diff: number;
  pct: number | null;
}

interface ChartPoint {
  label: string;
  curDate: string;
  prevDate: string;
  atual: number;
  anterior: number;
  [key: string]: string | number;
}

// ─── Constantes ────────────────────────────────────────────────────────────────

const ROWS_PER_PAGE = 50;
const COLOR_ATUAL    = '#3b82f6'; // blue-500
const COLOR_ANTERIOR = '#f472b6'; // pink-400

// ─── Helpers de formatação ─────────────────────────────────────────────────────

/** "2026-05-12" → "12/05/2026" */
const fmtShort = (iso: string): string => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const WEEKDAYS_PT = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];

/** "2026-05-12" → "12/05" (sem ano, para colunas de tabela) */
const fmtDay = (iso: string): string => {
  if (!iso) return '—';
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
};

/**
 * Dado um array de DailyPoints, retorna:
 *   - 1 dia  → "DD/MM/YYYY - Dia da Semana" (inclui dia da semana)
 *   - vários → "DD/MM a DD/MM/YYYY"
 */
const fmtRange = (points: DailyPoint[]): string => {
  if (!points.length) return '—';
  const dates = points.map(p => p.date).sort();
  const first = dates[0];
  const last  = dates[dates.length - 1];
  if (first === last) {
    const [y, m, d] = first.split('-');
    const wd = new Date(`${y}-${m}-${d}T12:00:00`);
    return `${d}/${m}/${y} - ${WEEKDAYS_PT[wd.getDay()]}`;
  }
  const [fy, fm, fd] = first.split('-');
  const [, lm, ld]   = last.split('-');
  return `${fd}/${fm} a ${ld}/${lm}/${fy}`;
};

// ─── Tooltip do gráfico de linhas ─────────────────────────────────────────────

interface ChartPayloadItem {
  dataKey?: string;
  name?: string;
  value?: number;
  color?: string;
  payload?: ChartPoint;
}

const ChartTooltip = ({ active, payload }: { active?: boolean; payload?: ChartPayloadItem[] }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload as ChartPoint;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-lg p-3 text-sm min-w-[180px]">
      {payload.map((item: ChartPayloadItem) => (
        <div key={item.dataKey as string} className="flex items-center gap-2 py-0.5">
          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
          <span className="text-gray-600 dark:text-gray-300 flex-1">{item.name}:</span>
          <span className="font-bold text-gray-900 dark:text-white">
            {(item.value ?? 0).toLocaleString('pt-BR')}
          </span>
        </div>
      ))}
      {point.curDate && point.prevDate && (
        <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-400 dark:text-gray-500">
          {fmtShort(point.curDate)} vs {fmtShort(point.prevDate)}
        </div>
      )}
    </div>
  );
};

// ─── Sub-componente: badge de variação ─────────────────────────────────────────

const DiffBadge: React.FC<{ diff: number; pct: number | null }> = ({ diff, pct }) => {
  if (diff === 0 || pct === null) {
    return (
      <span className="inline-flex items-center gap-1 text-gray-400 dark:text-gray-500">
        <Minus className="w-3.5 h-3.5" />
        —
      </span>
    );
  }
  const positive = diff > 0;
  const colorClass = positive
    ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20'
    : 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20';
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 font-semibold text-xs px-1.5 py-0.5 rounded ${colorClass}`}>
      <Icon className="w-3.5 h-3.5" />
      {positive ? '+' : ''}{pct.toFixed(1).replace('.', ',')}%
    </span>
  );
};

// ─── Componente principal ──────────────────────────────────────────────────────

export const ComparativeTable: React.FC<ComparativeTableProps> = ({
  current,
  previous,
  comparisonLabel,
  isYearOverYear = false,
  className = '',
}) => {
  const [page, setPage] = useState(0);

  const currentRangeLabel  = useMemo(() => fmtRange(current),  [current]);
  const previousRangeLabel = useMemo(() => fmtRange(previous), [previous]);

  const rows = useMemo((): TableRow[] => {
    const curSorted = [...current].sort((a, b) => a.date.localeCompare(b.date));

    if (isYearOverYear) {
      const prevMap = new Map(previous.map(p => [p.date, p]));
      return curSorted.map(cur => {
        const d = new Date(cur.date + 'T12:00:00');
        d.setFullYear(d.getFullYear() - 1);
        const prevDate = [
          d.getFullYear(),
          String(d.getMonth() + 1).padStart(2, '0'),
          String(d.getDate()).padStart(2, '0'),
        ].join('-');
        const prev  = prevMap.get(prevDate);
        const curP  = cur.people;
        const prevP = prev?.people ?? 0;
        const diff  = curP - prevP;
        return { curDate: cur.date, prevDate, cur: curP, prev: prevP, diff, pct: prevP > 0 ? (diff / prevP) * 100 : null };
      });
    }

    const prevSorted = [...previous].sort((a, b) => a.date.localeCompare(b.date));

    // Calcular shift em dias entre as duas séries (curSorted[0] − prevSorted[0]).
    // Isso garante que cada data do período atual seja comparada com a mesma
    // data do período anterior (ex.: domingo 17/05 ↔ domingo 10/05, com shift=7).
    let shiftDays = 0;
    if (curSorted.length > 0 && prevSorted.length > 0) {
      const ms =
        new Date(curSorted[0].date + 'T12:00:00').getTime() -
        new Date(prevSorted[0].date + 'T12:00:00').getTime();
      shiftDays = Math.round(ms / 86400000);
    }

    const subtractDaysISO = (iso: string, n: number): string => {
      const d = new Date(iso + 'T12:00:00');
      d.setDate(d.getDate() - n);
      return [
        d.getFullYear(),
        String(d.getMonth() + 1).padStart(2, '0'),
        String(d.getDate()).padStart(2, '0'),
      ].join('-');
    };

    const prevMap = new Map(prevSorted.map(p => [p.date, p.people]));

    return curSorted.map(cur => {
      const prevDate = shiftDays > 0 ? subtractDaysISO(cur.date, shiftDays) : '';
      const prevP   = prevDate ? (prevMap.get(prevDate) ?? 0) : 0;
      const curP    = cur.people;
      const diff    = curP - prevP;
      return {
        curDate:  cur.date,
        prevDate: (prevDate && prevMap.has(prevDate)) ? prevDate : '',
        cur:  curP,
        prev: prevP,
        diff,
        pct: prevP > 0 ? (diff / prevP) * 100 : null,
      };
    });
  }, [current, previous, isYearOverYear]);

  // Gráfico usa ordem cronológica (mais antigo à esquerda)
  const chartData = useMemo((): ChartPoint[] =>
    [...rows].sort((a, b) => a.curDate.localeCompare(b.curDate)).map(r => ({
      label:    fmtDay(r.curDate),
      curDate:  r.curDate,
      prevDate: r.prevDate,
      atual:    r.cur,
      anterior: r.prev,
    })),
  [rows]);

  const totals = useMemo(() => {
    const curTotal  = rows.reduce((acc, r) => acc + r.cur,  0);
    const prevTotal = rows.reduce((acc, r) => acc + r.prev, 0);
    const diff = curTotal - prevTotal;
    return { curTotal, prevTotal, diff, pct: prevTotal > 0 ? (diff / prevTotal) * 100 : null };
  }, [rows]);

  const totalPages = Math.ceil(rows.length / ROWS_PER_PAGE);
  // Tabela exibe do mais recente para o mais antigo (hoje primeiro)
  const rowsDesc  = useMemo(() => [...rows].sort((a, b) => b.curDate.localeCompare(a.curDate)), [rows]);
  const pagedRows = rowsDesc.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);

  if (rows.length === 0) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 ${className}`}>
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>Sem dados no período selecionado</p>
        </div>
      </div>
    );
  }

  const totalPositive = totals.diff >= 0;
  const totalColorClass = totalPositive
    ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-red-600 dark:text-red-400';

  const multiDay = rows.length > 1;

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-4 sm:p-6 ${className}`}>

      {/* ── Header ── */}
      <div className="mb-5">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-600" />
          Comparativo de Fluxo
        </h3>

        <div className="mt-3 flex flex-col sm:flex-row gap-2 text-sm">
          <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
            <span className="w-2.5 h-2.5 rounded-full bg-blue-500 shrink-0" />
            <span className="text-blue-700 dark:text-blue-300 font-medium">Período atual:</span>
            <span className="text-blue-900 dark:text-blue-100 font-bold">{currentRangeLabel}</span>
          </div>
          <div className="hidden sm:flex items-center text-gray-400 dark:text-gray-500 text-xs px-1 self-center">vs</div>
          <div className="flex items-center gap-2 px-3 py-2 bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-700 rounded-lg">
            <span className="w-2.5 h-2.5 rounded-full bg-pink-400 shrink-0" />
            <span className="text-pink-700 dark:text-pink-300 font-medium">{comparisonLabel}:</span>
            <span className="text-pink-900 dark:text-pink-100 font-bold">{previousRangeLabel}</span>
          </div>
        </div>
      </div>

      {/* ── Cards de resumo ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 sm:p-3 border border-blue-100 dark:border-blue-800 flex sm:flex-col items-center sm:items-center justify-between sm:justify-start gap-2 sm:gap-0 sm:text-center">
          <p className="text-xs text-blue-600 dark:text-blue-400 font-medium uppercase tracking-wide sm:mb-1">Período Atual</p>
          <div className="flex items-baseline gap-2 sm:block sm:text-center">
            <p className="text-xl sm:text-2xl font-bold text-blue-700 dark:text-blue-300">{totals.curTotal.toLocaleString('pt-BR')}</p>
            <p className="text-xs text-blue-500 dark:text-blue-400 sm:mt-0.5">pessoas</p>
          </div>
        </div>

        <div className={`rounded-xl p-3 border flex sm:flex-col items-center sm:items-center justify-between sm:justify-start gap-2 sm:gap-0 sm:text-center ${
          totalPositive
            ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800'
            : 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800'
        }`}>
          <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide sm:mb-1">Variação</p>
          <div className="flex items-baseline gap-2 sm:block sm:text-center">
            <p className={`text-xl sm:text-2xl font-bold ${totalColorClass}`}>
              {totals.pct !== null
                ? `${totalPositive ? '+' : ''}${totals.pct.toFixed(1).replace('.', ',')}%`
                : '—'}
            </p>
            <p className={`text-xs sm:mt-0.5 ${totalColorClass}`}>
              {totalPositive ? '+' : ''}{totals.diff.toLocaleString('pt-BR')} pessoas
            </p>
          </div>
        </div>

        <div className="bg-pink-50 dark:bg-pink-900/20 rounded-xl p-3 border border-pink-100 dark:border-pink-800 flex sm:flex-col items-center sm:items-center justify-between sm:justify-start gap-2 sm:gap-0 sm:text-center">
          <p className="text-xs text-pink-600 dark:text-pink-400 font-medium uppercase tracking-wide sm:mb-1">Período Anterior</p>
          <div className="flex items-baseline gap-2 sm:block sm:text-center">
            <p className="text-xl sm:text-2xl font-bold text-pink-600 dark:text-pink-300">{totals.prevTotal.toLocaleString('pt-BR')}</p>
            <p className="text-xs text-pink-500 dark:text-pink-400 sm:mt-0.5">pessoas</p>
          </div>
        </div>
      </div>

      {/* ── Gráfico de linhas (só quando múltiplos dias) ── */}
      {multiDay && (
        <div className="mb-6 w-full overflow-hidden" style={{ minHeight: 0 }}>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData} margin={{ top: 5, right: 8, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={false}
                width={40}
              />
              <RechartsTooltip content={<ChartTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: '12px', paddingTop: '8px', color: '#9ca3af' }}
              />
              <Line
                type="monotone"
                dataKey="atual"
                name="Período atual"
                stroke={COLOR_ATUAL}
                strokeWidth={2.5}
                dot={{ fill: COLOR_ATUAL, r: 4, strokeWidth: 0 }}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="anterior"
                name="Período anterior"
                stroke={COLOR_ANTERIOR}
                strokeWidth={2.5}
                dot={{ fill: COLOR_ANTERIOR, r: 4, strokeWidth: 0 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Tabela detalhada — estilo planilha ── */}
      {multiDay && (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 -mx-1">
          <table className="w-full text-sm border-collapse" style={{ minWidth: '580px' }}>
            <colgroup>
              <col style={{ width: '22%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '22%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '17%' }} />
              <col style={{ width: '17%' }} />
            </colgroup>

            <thead>
              {/* Linha de grupos — faixas coloridas */}
              <tr>
                <th
                  colSpan={2}
                  className="px-3 py-1.5 text-center text-xs font-bold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700/50 uppercase tracking-wider"
                >
                  Período Atual
                </th>
                <th
                  colSpan={2}
                  className="px-3 py-1.5 text-center text-xs font-bold text-pink-600 dark:text-pink-300 bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-700/50 uppercase tracking-wider"
                >
                  Período Anterior
                </th>
                <th
                  colSpan={2}
                  className="px-3 py-1.5 text-center text-xs font-bold text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 uppercase tracking-wider"
                >
                  Comparativo
                </th>
              </tr>

              {/* Sub-cabeçalhos das colunas */}
              <tr className="bg-gray-100 dark:bg-gray-700/80">
                <th className="px-3 py-2 text-left text-xs font-semibold text-blue-600 dark:text-blue-400 border border-gray-200 dark:border-gray-600 uppercase tracking-wide">
                  Data
                </th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-blue-600 dark:text-blue-400 border border-gray-200 dark:border-gray-600 uppercase tracking-wide whitespace-nowrap">
                  Pessoas
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-pink-500 dark:text-pink-400 border border-gray-200 dark:border-gray-600 uppercase tracking-wide">
                  Data
                </th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-pink-500 dark:text-pink-400 border border-gray-200 dark:border-gray-600 uppercase tracking-wide whitespace-nowrap">
                  Pessoas
                </th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 dark:text-gray-300 border border-gray-200 dark:border-gray-600 uppercase tracking-wide whitespace-nowrap">
                  Dif.
                </th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 dark:text-gray-300 border border-gray-200 dark:border-gray-600 uppercase tracking-wide">
                  %
                </th>
              </tr>
            </thead>

            <tbody className="bg-white dark:bg-gray-800">
              {pagedRows.map((r, idx) => (
                <tr
                  key={r.curDate || r.prevDate || idx}
                  className={`hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors ${
                    idx % 2 !== 0 ? 'bg-gray-50/80 dark:bg-gray-700/20' : ''
                  }`}
                >
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-200 border border-gray-100 dark:border-gray-700 whitespace-nowrap">
                    {r.curDate ? (
                      <>
                        <span className="sm:hidden">{fmtDay(r.curDate)}</span>
                        <span className="hidden sm:inline">{formatDateWithWeekday(r.curDate)}</span>
                      </>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-bold text-blue-700 dark:text-blue-300 tabular-nums border border-gray-100 dark:border-gray-700 whitespace-nowrap">
                    {r.cur.toLocaleString('pt-BR')}
                  </td>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400 border border-gray-100 dark:border-gray-700 whitespace-nowrap">
                    {r.prevDate ? (
                      <>
                        <span className="sm:hidden">{fmtDay(r.prevDate)}</span>
                        <span className="hidden sm:inline">{formatDateWithWeekday(r.prevDate)}</span>
                      </>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-pink-600 dark:text-pink-300 tabular-nums border border-gray-100 dark:border-gray-700 whitespace-nowrap">
                    {r.prev > 0 ? r.prev.toLocaleString('pt-BR') : '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-medium text-gray-700 dark:text-gray-200 tabular-nums border border-gray-100 dark:border-gray-700 whitespace-nowrap">
                    {r.diff > 0 ? '+' : ''}{r.diff.toLocaleString('pt-BR')}
                  </td>
                  <td className="px-3 py-2 text-right border border-gray-100 dark:border-gray-700">
                    <DiffBadge diff={r.diff} pct={r.pct} />
                  </td>
                </tr>
              ))}
            </tbody>

            <tfoot className="bg-gray-100 dark:bg-gray-700/60 border-t-2 border-gray-300 dark:border-gray-500">
              <tr>
                <td className="px-3 py-2.5 font-bold text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600">
                  Total
                </td>
                <td className="px-3 py-2.5 text-right font-bold text-blue-700 dark:text-blue-300 tabular-nums border border-gray-200 dark:border-gray-600 whitespace-nowrap">
                  {totals.curTotal.toLocaleString('pt-BR')}
                </td>
                <td className="px-3 py-2.5 border border-gray-200 dark:border-gray-600" />
                <td className="px-3 py-2.5 text-right font-bold text-pink-600 dark:text-pink-300 tabular-nums border border-gray-200 dark:border-gray-600 whitespace-nowrap">
                  {totals.prevTotal.toLocaleString('pt-BR')}
                </td>
                <td className="px-3 py-2.5 text-right font-bold text-gray-700 dark:text-gray-200 tabular-nums border border-gray-200 dark:border-gray-600 whitespace-nowrap">
                  {totals.diff > 0 ? '+' : ''}{totals.diff.toLocaleString('pt-BR')}
                </td>
                <td className="px-3 py-2.5 text-right border border-gray-200 dark:border-gray-600">
                  <DiffBadge diff={totals.diff} pct={totals.pct} />
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-gray-500 dark:text-gray-400">
            Página {page + 1} de {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <button
              type="button"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Próxima
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
